import React, { useEffect, useRef, useState } from 'react';
import {
  Container,
  Group,
  Button,
  Text,
  Grid,
  Title,
  SimpleGrid,
  Card,
  ThemeIcon,
  Transition,
  Box,
  Image,
  List,
  Avatar,
  Rating,
  SegmentedControl,
  Accordion,
  ActionIcon,
  Badge,
} from '@mantine/core';
import { Link, useNavigate } from 'react-router-dom';
import Icon from 'src/components/common/Icons/Icons/Icon';
import { CustomButton } from 'src/components/common/Buttons/CustomButton';
import useViewportHeight from 'src/hooks/useViewPortHeight';
import { authRoutes } from 'src/navigators/routes/auth';
import useResponsive from 'src/hooks/useResponsive';
import ChatModal from 'src/components/common/Modals/ChatModal';
import MaintenanceNotice from 'src/components/common/Modals/MaintenanceNotice';
// import UserInfoModal from 'src/components/common/Modals/UserInfoModal';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentSection, setCurrentSection] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useViewportHeight();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const sections = [
    { id: 'hero', component: HeroSection, label: 'Home', background: '' },
    {
      id: 'why-choose-us',
      component: WhyChooseUsSection,
      label: 'Why Choose Us',
      background: 'bg-gradient-to-r from-orange-50 to-white',
    },
    {
      id: 'learners',
      component: ForLearnersSection,
      label: 'Learners',
      background: 'bg-gradient-to-r from-orange-50 to-white',
    },
    {
      id: 'parents',
      component: ForParentsSection,
      label: 'Parents',
      background: 'bg-gradient-to-r from-orange-50 to-white',
    },
    {
      id: 'testimonials',
      component: TestimonialsSection,
      label: 'Testimonials',
      background: 'bg-gradient-to-r from-orange-50 to-white',
    },
    {
      id: 'pricing',
      component: PricingSection,
      label: 'Pricing',
      background: 'bg-primary',
    },
    { id: 'faq', component: FAQSection, label: 'FAQ', background: '' },
  ];

  const scrollToSection = (index: number) => {
    const container = scrollContainerRef.current;
    if (container) {
      const sectionElement = container.children[index] as HTMLElement;
      sectionElement.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false); // Close mobile menu when navigating
  };

  return (
    <div className='flex flex-col h-[100dvh] w-[100dvw]'>
      <header className='sticky w-full bg-white z-20'>
        {/* Desktop Header */}
        <div className='hidden desktop-up:flex flex-row items-center justify-between px-10 py-4'>
          <div className='flex flex-1 flex-row items-center gap-x-4 xl:gap-x-8'>
            <Image
              className='max-w-28'
              src='/landing/logo.png'
              alt='Chat Interface'
            />
          </div>
          <div className='flex flex-row items-center gap-x-1'>
            {sections.map((section, index) => (
              <CustomButton
                key={section.id}
                onClick={() => scrollToSection(index)}
                variant='ghost'
                size='sm'
                width='w-fit'
                className='cursor-pointer whitespace-nowrap relative group'
                textClass='text-primary font-quarterbold'
              >
                <span className='relative'>
                  {section.label}
                  <span className='absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 ease-out group-hover:w-full'></span>
                </span>
              </CustomButton>
            ))}
          </div>
          <div className='flex flex-row gap-3 items-center'>
            <div
              onClick={() => {
                window.open('https://wa.me/6597766164', '_blank');
              }}
              className='cursor-pointer flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-green-50 transition-colors duration-200'
            >
              <Icon name='whatsapp' color='green' size={'18'} />
              <Text className='text-green-600 font-medium text-sm hidden lg:block'>
                WhatsApp Us
              </Text>
            </div>
            <CustomButton
              onClick={() => {
                window.open(
                  process.env.REACT_APP_FRONTEND_URL + '/auth/login',
                  '_blank'
                );
              }}
              variant='default'
              size='md'
              width='w-24'
            >
              <Text className='text-white font-semibold text-sm'>Login</Text>
            </CustomButton>
          </div>
        </div>

        {/* Mobile Header */}
        <div className='flex flex-row desktop-up:hidden items-center justify-between px-8 py-4 relative'>
          {/* Logo */}
          <div className='flex-1'>
            <Image
              className='max-w-20 sm:max-w-24'
              src='/landing/logo.png'
              alt='Chat Interface'
            />
          </div>

          {/* Burger Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className='p-2 rounded-lg hover:bg-gray-100 transition-colors duration-300'
            aria-label='Menu'
          >
            <Icon
              name={mobileMenuOpen ? 'cross' : 'menu'}
              color='primary'
              size={'24'}
            />
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        <Transition
          mounted={mobileMenuOpen}
          transition='slide-down'
          duration={300}
          timingFunction='ease'
        >
          {(styles) => (
            <div
              style={styles}
              className='desktop-up:hidden absolute top-full left-0 w-full bg-white shadow-lg border-t z-10'
            >
              <div className='flex flex-col px-8 py-4'>
                {sections.map((section, index) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(index)}
                    className='flex items-center py-3 text-left hover:bg-gray-50 transition-colors duration-200 border-b border-gray-200'
                  >
                    <Text className='text-primary font-medium'>
                      {section.label}
                    </Text>
                  </button>
                ))}
                <div className='flex flex-col gap-3 py-4 mt-2'>
                  <div
                    onClick={() => {
                      window.open('https://wa.me/6597766164', '_blank');
                    }}
                    className='cursor-pointer flex items-center justify-center gap-2 px-3 py-2 rounded-lg hover:bg-green-50 transition-colors duration-200 border border-green-200'
                  >
                    <Icon name='whatsapp' color='green' size={'18'} />
                    <Text className='text-green-600 font-medium text-sm'>
                      WhatsApp Us
                    </Text>
                  </div>
                  <CustomButton
                    onClick={() => {
                      window.open(
                        process.env.REACT_APP_FRONTEND_URL + '/auth/login',
                        '_blank'
                      );
                    }}
                    variant='default'
                    size='md'
                    width='w-full'
                  >
                    <Text className='text-white font-semibold text-sm'>
                      Login
                    </Text>
                  </CustomButton>
                </div>
              </div>
            </div>
          )}
        </Transition>
      </header>

      <div
        ref={scrollContainerRef}
        className='snap-y snap-mandatory overflow-y-auto flex-1 h-full'
      >
        {sections.map(({ id, component: Section, background }, index) => (
          <section
            key={id}
            id={id}
            className={`snap-center snap-always ${background} h-full overflow-hidden p-8 md:px-10 md:py-10`}
          >
            <Section />
          </section>
        ))}
        <div className='snap-start'>
          <Footer />
        </div>
      </div>
    </div>
  );
};

const HeroSection: React.FC = () => {
  const navigate = useNavigate();
  const [userInfoModalOpened, setUserInfoModalOpened] = useState(false);
  const handleChatModalClose = () => {
    setUserInfoModalOpened(false);
  };
  return (
    <>
      {/* <UserInfoModal opened={userInfoModalOpened} onClose={() => setUserInfoModalOpened(false)} /> */}
      <ChatModal opened={userInfoModalOpened} onClose={handleChatModalClose} />
      {/* <MaintenanceNotice
        opened={userInfoModalOpened}
        onClose={handleChatModalClose}
      /> */}
      <div className='h-full w-full flex flex-col gap-y-6 md:gap-y-10'>
        <Text className='text-center text-[1.2rem] xs:text-[1.6rem] md:text-[2rem] lg:text-[2.4rem] xl:text-[2.8rem] 2xl:text-[3.6rem] text-primary font-bold'>
          Introducing Singapore's First Gen-AI Designed for Learning
        </Text>

        <div className='flex-1 flex flex-col gap-y-6 md:gap-y-0 md:gap-x-6 lg:gap-x-16 md:flex-row items-center'>
          {/* Logo div */}
          <div className='w-full max-w-[35%] md:max-w-[35%] lg:max-w-[28%] h-auto justify-center lg:flex-shrink-0'>
            <Image
              className='w-full h-auto object-contain'
              src='/landing/logo.png'
              alt='Chat Interface'
            />
          </div>

          {/* Container Div */}
          <div
            style={{
              backgroundImage: 'url(/images/doodles.png)',
              backgroundSize: '800px',
              backgroundPosition: 'center',
              backgroundRepeat: 'repeat',
            }}
            className='w-full flex-1 flex flex-col items-center justify-center h-full py-4 border-primary rounded-2xl sm:rounded-3xl border-4 sm:border-6 gap-y-10'
          >
            {/* Logo div */}
            <div className='flex flex-col items-center'>
              <img
                src='/images/favicon.png'
                alt='MYAi Brain'
                className='w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16'
              />
            </div>

            {/* Try Button */}
            <div className='w-[90%] sm:w-[85%] md:w-[80%] lg:w-[70%] xl:w-[60%]'>
              <CustomButton
                onClick={() => setUserInfoModalOpened(true)}
                className='w-full py-5 sm:py-6 md:py-8 rounded-lg border-2 border-yellow-500 hover:bg-primary/90 hover:scale-105 hover:shadow-lg transition-all duration-500 ease-out transform'
                textClass='text-base xs:text-lg sm:text-xl md:text-[1.5rem] 2xl:text-[1.8rem]'
              >
                {' '}
                TRY FREE NOW!{' '}
              </CustomButton>
            </div>

            {/* Signin Options */}
            <div className='w-[85%] sm:w-[80%] md:w-[75%] lg:w-[65%] xl:w-[55%] flex flex-col gap-y-4 items-center'>
              <CustomButton
                onClick={() => {
                  window.open(
                    process.env.REACT_APP_FRONTEND_URL + '/auth/login',
                    '_blank'
                  );
                }}
                variant='default'
                size='sm'
                width='w-full lg:w-[75%]'
                className='border-2 py-3 sm:py-5 md:py-7 hover:bg-primary/90 hover:scale-105 hover:shadow-lg transition-all duration-500 ease-out transform'
              >
                <Text className='text-white font-semibold text-xxs xs:text-xs sm:text-sm md:text-base'>
                  Already have an account? Log in
                </Text>
              </CustomButton>

              <Text
                onClick={() => {
                  window.open(
                    process.env.REACT_APP_FRONTEND_URL + '/auth/signup',
                    '_blank'
                  );
                }}
                className='text-black font-medium text-xxs xs:text-xs sm:text-sm md:text-base underline cursor-pointer hover:text-primary transition-colors duration-200'
              >
                Don't have an account? Sign up
              </Text>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const WhyChooseUsSection: React.FC = () => (
  <div className='h-full w-full flex flex-col items-center justify-center'>
    {/* <div className="w-full md:w-[85%] "> */}
    <Text
      align='center'
      className='mb-8 font-bold text-black text-lg md:text-xl lg:text-2xl'
    >
      Why Choose Us?
    </Text>
    <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
      {[
        {
          image: '/landing/SG2.png',
          alt: 'Curriculum',
          title: "Aligned To MOE's Curriculum",
          description:
            "Content and how learners are guided through the system are aligned to MOE's curriculum and teaching methods.",
          bgColor: 'bg-primary',
        },
        {
          image: '/landing/chatgroup.png',
          alt: 'Personalized',
          title: 'Personalised Guidance',
          description:
            "Our technology provides personalised guidance based on each learners' needs. The guidance methods are informed by extensive research on learning.",
          bgColor: 'bg-teal-500',
        },
        {
          image: '/landing/adaptivesystem.png',
          alt: 'Adaptive',
          title: 'Adaptive System',
          description:
            "A first of its kind adaptive system that provides guidance by creating a learning path unique to each learners' interactions.",
          bgColor: 'bg-green-600',
        },
      ].map((card, index) => (
        <div
          key={index}
          className={`${card.bgColor} shadow-sm flex flex-col md:flex-col items-center rounded-xl px-4 py-4 lg:py-0 xl:py-4 justify-center h-fit lg:h-96`}
        >
          <div className='flex flex-row lg:flex-col items-center w-full lg:h-3/4'>
            <div className='w-10 h-10 sm:w-20 sm:h-20 flex items-center justify-center mb-0 lg:mb-4 mr-4 lg:mr-0'>
              <img
                src={card.image}
                alt={card.alt}
                className='w-20 h-20 object-contain'
              />
            </div>
            <div className='flex-1 max-h-18 sm:max-h-full overflow-hidden lg:text-center'>
              <Text
                weight={500}
                className='mb-2 lg:mb-4 text-white hidden sm:block text-lg lg:text-xl font-bold'
              >
                {card.title}
              </Text>
              <Text className='text-white text-xs md:text-base'>
                {card.description}
              </Text>
            </div>
          </div>
        </div>
      ))}
    </div>
    {/* </div> */}
  </div>
);

const ForLearnersSection: React.FC = () => {
  const navigate = useNavigate();
  return (
    <div className='h-full flex items-center justify-center'>
      <div className='w-full grid grid-cols-1 lg:grid-cols-2 gap-8'>
        <div className='hidden lg:flex items-center justify-center order-last md:order-first'>
          <Image
            src='/landing/learnerCollage.png'
            alt='Learners using MYAi'
            className='max-h-[500px] max-w-[500px] object-contain'
          />
        </div>
        <div className='flex flex-col justify-center'>
          <Text size='sm' color='dimmed' className='mb-2'>
            For Learners
          </Text>
          <Text className='mb-4 text-lg md:text-xl lg:text-2xl font-bold'>
            Turning "I don't know" to "Aha" moments
          </Text>
          <Text className='mb-6 text-xs md:text-md lg:text-base'>
            With MYAi, you can ask questions and be guided one step at a time.
            Imagine having someone who can guide you anytime you need it!
          </Text>
          <Card shadow='sm' padding='md' radius='md' className='mb-4'>
            <Group>
              <div>
                <Text className='text-primary text-base md:text-lg lg:text-xl font-bold'>
                  Ask a Question
                </Text>
                <Text
                  size='sm'
                  color='dimmed'
                  className='text-xs md:text-md lg:text-base'
                >
                  Upload a question and MYAi will guide you step by step.
                </Text>
              </div>
            </Group>
          </Card>
          <Card shadow='sm' padding='md' radius='md'>
            <Group>
              <div>
                <Text className='text-primary text-base md:text-lg lg:text-xl font-bold'>
                  Targeted Learning
                </Text>
                <Text
                  size='sm'
                  color='dimmed'
                  className='text-xs md:text-md lg:text-base'
                >
                  Maximise your study time by focusing on questions you the need
                  help in.
                </Text>
              </div>
            </Group>
          </Card>
          <CustomButton
            onClick={() => {
              window.open(process.env.REACT_APP_FRONTEND_URL, '_blank');
            }}
            variant='default'
            size='md'
            width='w-full md:w-2/4 my-10'
          >
            Try Now
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

const ForParentsSection: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className='h-full items-center justify-center w-full grid grid-cols-1 lg:grid-cols-2 gap-8'>
      {/* <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-8"> */}
      <div className='flex flex-col justify-center'>
        <Text color='dimmed' className='text-sm mb-2'>
          For Parents
        </Text>
        <Text className='mb-4 text-lg md:text-xl lg:text-2xl font-bold'>
          Help your child progress with personalised guidance
        </Text>
        <Text className='mb-6 text-sm md:text-lg lg:text-base'>
          Using an encouraging and conversational tone, MYAi provides your child
          with guidance based on their learning needs.
        </Text>
        <List
          spacing='xl'
          size='sm'
          center
          icon={
            <ThemeIcon className='bg-blue-100' size={24} radius='xl'>
              <Icon name='check' color='blue' size={'14'} />
            </ThemeIcon>
          }
        >
          <List.Item className='text-sm md:text-md lg:text-base'>
            Support your child with the first-of-its-kind personalised learning
            system.
          </List.Item>
          <List.Item className='text-sm md:text-md lg:text-base'>
            Monitor your child's progress through the chats.
          </List.Item>
          <List.Item className='text-sm md:text-md lg:text-base'>
            Ensure your child is exposed to credible content in a safe space.
          </List.Item>
          <List.Item className='text-sm md:text-md lg:text-base'>
            Obtain help from our friendly agents 24/7.
          </List.Item>
        </List>
        <CustomButton
          onClick={() => {
            window.open(process.env.REACT_APP_FRONTEND_URL, '_blank');
          }}
          variant='default'
          size='md'
          width='w-full md:w-2/4 mt-6'
        >
          Get Started
        </CustomButton>
      </div>
      <div className='hidden lg:flex items-center justify-center'>
        <Image
          src='/landing/family.png'
          alt='Happy family using MYAi'
          className='max-h-[500px] max-w-[500px] object-contain'
        />
      </div>
    </div>
    // </div>
  );
};

const TestimonialsSection: React.FC = () => {
  const { isMobile } = useResponsive();
  const testimonials = [
    {
      name: 'Shu Ting',
      avatar: '/landing/testimonial.png',
      rating: 5,
      subtitle: 'Parent of P4 Science Learner',
      comment:
        'This solution REVOLUTIONIZES education! Now every child has access to high quality learning experience.',
    },
    {
      name: 'Jacob Lee',
      avatar: '/landing/testimonial2.png',
      rating: 4,
      subtitle: 'Parent of P3 Science Learner',
      comment:
        'MYAi talks to me just like my science teacher. I feel like I can ask any questions and MYAi will guide me.',
    },
    {
      name: 'Sharadha',
      avatar: '/landing/testimonial3.png',
      rating: 5,
      subtitle: 'Parent of P4 Science Learner',
      comment:
        'It helps me to know what my son is learning. My son learns how to use new technology to help him.  I hope we can do this for more subjects.',
    },
  ];

  return (
    <div className='h-full flex flex-col items-center justify-center'>
      <div className='w-full flex flex-col items-center gap-2 md:gap-4'>
        <Text className='text-lg md:text-xl lg:text-2xl mb-8 font-bold text-center'>
          What Have They Been Saying About Us?
        </Text>

        {/* Mobile: Single column with scroll, Tablet and up: Grid layout */}
        <div className='w-full px-2 md:px-4'>
          {/* Mobile Layout - Single column with vertical scroll */}
          <div className='flex md:hidden flex-col gap-2 max-h-[400px] overflow-y-auto'>
            {testimonials.map((testimonial, index) => (
              <Card
                key={index}
                shadow='sm'
                radius='md'
                className='flex-shrink-0 p-3'
              >
                <Group position='apart' mb='xs'>
                  <Text className='font-bold text-sm'>{testimonial.name}</Text>
                  <Rating value={testimonial.rating} readOnly size='xs' />
                </Group>
                <Text className='font-light text-xs mb-1'>
                  {testimonial.subtitle}
                </Text>
                <Text className='text-xs line-clamp-2' color='dimmed'>
                  {testimonial.comment}
                </Text>
              </Card>
            ))}
          </div>

          {/* Tablet and Desktop Layout - Grid */}
          <div className='hidden md:grid md:grid-cols-3 gap-3 lg:gap-4'>
            {testimonials.map((testimonial, index) => (
              <Card
                key={index}
                shadow='sm'
                radius='md'
                className='flex flex-col justify-between h-[220px] lg:h-[250px] p-3'
              >
                <div>
                  <Group position='apart' mb='xs'>
                    <Text className='font-bold text-sm lg:text-base'>
                      {testimonial.name}
                    </Text>
                    <Rating value={testimonial.rating} readOnly size='sm' />
                  </Group>
                  <Text className='font-light text-xs lg:text-sm mb-2'>
                    {testimonial.subtitle}
                  </Text>
                </div>
                <Text
                  className='text-xs lg:text-sm flex-1'
                  color='dimmed'
                  style={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {testimonial.comment}
                </Text>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const PricingSection: React.FC = () => {
  return (
    <div className='h-full flex flex-col items-center justify-center'>
      <div className='w-full flex flex-col items-center justify-center gap-3 pricing-section-mobile:gap-4 md:gap-6 lg:gap-8'>
        <Text
          align='center'
          className='font-bold text-white text-lg md:text-xl lg:text-2xl leading-tight pricing-section-mobile-down:mb-2'
        >
          Choose the Perfect Plan for Your Learning Journey
        </Text>
        <div className='w-full flex flex-col pricing-section-mobile:flex-row justify-center items-center pricing-section-mobile:items-stretch gap-4 pricing-section-mobile:gap-6 lg:gap-8'>
          <FreePlanCard />
          <PremiumPlanCard />
        </div>
      </div>
    </div>
  );
};

const FreePlanCard: React.FC = () => {
  const navigate = useNavigate();
  const features = [
    'Limited questions per day',
    'Text-based questions only',
    'Basic support',
  ];
  const missingFeatures = [
    'Image and document upload',
    'Access to question bank',
    '24/7 priority support',
  ];

  return (
    <div className='w-full h-auto min-h-[180px] pricing-section-mobile-down:min-h-[200px] pricing-section-mobile:max-h-[280px] flex flex-col py-2 pricing-section-mobile-down:py-2 pricing-section-mobile:py-4 lg:py-6 px-2 pricing-section-mobile-down:px-2 pricing-section-mobile:px-4 lg:px-6 justify-between items-center bg-gray-100 rounded-lg pricing-section-mobile-down:rounded-xl lg:rounded-2xl'>
      <div className='flex-1 w-full'>
        <div className='flex flex-col items-center justify-center mb-1 pricing-section-mobile-down:mb-2 pricing-section-mobile:mb-4 lg:mb-6'>
          <Text className='text-sm pricing-section-mobile-down:text-base pricing-section-mobile:text-xl lg:text-2xl xl:text-3xl font-semibold text-gray-700 text-center'>
            Free
          </Text>
          <Text className='text-[10px] pricing-section-mobile-down:text-xs pricing-section-mobile:text-sm lg:text-base xl:text-lg text-gray-600 text-center mt-1 leading-tight'>
            Basic features for individual users
          </Text>
        </div>

        <div className='space-y-1 pricing-section-mobile-down:space-y-1 mb-1 pricing-section-mobile-down:mb-2 pricing-section-mobile:mb-4'>
          {missingFeatures.map((feature, index) => (
            <div key={index} className='flex items-center justify-center'>
              <Text className='text-gray-500 text-[9px] pricing-section-mobile-down:text-[10px] pricing-section-mobile:text-sm lg:text-base xl:text-lg line-through text-center leading-tight'>
                {feature}
              </Text>
            </div>
          ))}
        </div>
      </div>

      <CustomButton
        variant='outline'
        size='sm'
        onClick={() => {
          window.open(process.env.REACT_APP_FRONTEND_URL, '_blank');
        }}
        width='w-full'
        className='h-7 pricing-section-mobile-down:h-8 pricing-section-mobile:h-12'
        textClass='text-[10px] pricing-section-mobile-down:text-xs pricing-section-mobile:text-sm lg:text-base'
      >
        Try for Free
      </CustomButton>
    </div>
  );
};

const PremiumPlanCard: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const features = [
    'Access to the chatbot interface',
    'Ask question using text or image',
    'Unlimited number of questions per day',
    'Access to the question bank',
  ];

  return (
    <div className='w-full h-auto min-h-[180px] pricing-section-mobile-down:min-h-[200px] pricing-section-mobile:min-h-[400px] flex flex-col py-2 pricing-section-mobile-down:py-2 pricing-section-mobile:py-4 lg:py-6 px-2 pricing-section-mobile-down:px-2 pricing-section-mobile:px-4 lg:px-6 justify-between bg-white rounded-lg pricing-section-mobile-down:rounded-xl lg:rounded-2xl shadow-lg border-2 border-green-700'>
      <div className='flex flex-col justify-center mb-1 pricing-section-mobile-down:mb-2 pricing-section-mobile:mb-4 lg:mb-6'>
        {/* Title and HOT badge row */}
        <div className='flex flex-row items-center justify-between gap-2'>
          <Text className='text-sm pricing-section-mobile-down:text-base pricing-section-mobile:text-xl lg:text-2xl xl:text-3xl font-semibold text-primary'>
            Premium
          </Text>
          <Badge
            py={'xs'}
            px={'xs'}
            className='bg-red-500 text-white text-[8px] pricing-section-mobile-down:text-[9px] pricing-section-mobile:text-xs lg:text-sm'
          >
            HOT
          </Badge>
        </div>

        {/* NOW badge and pricing row */}
        <div className='flex flex-row items-center justify-between gap-2 mt-1 pricing-section-mobile-down:mt-1 lg:mt-2'>
          <Badge
            py={'xs'}
            px={'xs'}
            className='bg-green-700 text-white text-[8px] pricing-section-mobile-down:text-[9px] pricing-section-mobile:text-xs lg:text-sm'
          >
            NOW
          </Badge>
          <div className='flex flex-row items-center gap-1'>
            <Text className='text-xs pricing-section-mobile-down:text-sm pricing-section-mobile:text-lg lg:text-xl xl:text-2xl font-bold leading-tight'>
              SGD $51.3
              <span className='text-[9px] pricing-section-mobile-down:text-[10px] pricing-section-mobile:text-base lg:text-lg xl:text-xl font-bold'>
                /month
              </span>
            </Text>
            <Text className='text-[9px] pricing-section-mobile-down:text-[10px] pricing-section-mobile:text-sm lg:text-base xl:text-lg font-semibold line-through text-gray-500'>
              SGD $88
            </Text>
          </div>
        </div>
      </div>

      <div className='flex-1'>
        <Text className='text-[9px] pricing-section-mobile-down:text-[10px] pricing-section-mobile:text-sm lg:text-base xl:text-lg text-gray-600 mb-1 pricing-section-mobile-down:mb-1 pricing-section-mobile:mb-3 lg:mb-4 leading-tight'>
          Advanced features for serious learners
        </Text>

        <div className='space-y-1 pricing-section-mobile-down:space-y-1'>
          {features.map((feature, index) => (
            <div key={index} className='flex items-center'>
              <ThemeIcon
                color='#29748F'
                size={12}
                radius='xl'
                className='mr-1 flex-shrink-0'
              >
                <Icon name='check' size='8' />
              </ThemeIcon>
              <Text className='text-[9px] pricing-section-mobile-down:text-[10px] pricing-section-mobile:text-sm lg:text-base xl:text-lg leading-tight'>
                {feature}
              </Text>
            </div>
          ))}
        </div>
      </div>

      <CustomButton
        variant='default'
        color='blue'
        size='sm'
        width='w-full'
        onClick={() => {
          window.open(process.env.REACT_APP_FRONTEND_URL, '_blank');
        }}
        className='mt-1 pricing-section-mobile-down:mt-2 pricing-section-mobile:mt-4 lg:mt-6 h-7 pricing-section-mobile-down:h-8 pricing-section-mobile:h-12'
        textClass='text-[10px] pricing-section-mobile-down:text-xs pricing-section-mobile:text-sm lg:text-base'
      >
        Get Started
      </CustomButton>
    </div>
  );
};

// ... rest of the file remains unchanged ...

const FAQSection: React.FC = () => {
  const faqs = [
    {
      question: 'What is MYAi? How does it work?',
      answer:
        "MYAi is an intelligent tutoring system designed by a group of EdTech enthusiasts with strong content knowledge. For learners and families, MYAi offers encouraging and MOE-aligned guidance for students. It is ethically designed with safety and learning as top priority. MYAi is the only Generative AI powered tool that is available in Singapore. It covers the Primary Science subject at this beta stage. Your child can get personalised guidance on every question he asks. Unlike other AI tools such as ChatGPT, MYAi doesn't give learners the answers. Instead with limitless patience and well-known teaching methods, it guides learners towards the answer. Based on the needs of every learner, similar types of questions are proposed for students to master the concepts.",
    },
    {
      question: 'How do I get MYAi? Can anyone use it?',
      answer:
        'MYAi is a web-based platform that is accessible by all learners. Each learner will require a parent to create an account. Parent should sign up for one account for each child using so that the guidance given to each child is tailored to their specific level and learning needs.',
    },
    {
      question: 'How do I sign up for MYAi?',
      answer: `You may create an account with MYAi through the <insert link> to access the Premium Account for free for 7 days. After the 7 day free trial, users will get access to the Basic Account. Subscribe to the monthly or annual plans, to continue to use the Premium Account.`,
    },
    {
      question: 'What is the difference between the Premium and Basic Account?',
      answer:
        'In the Premium account, each parent can create multiple learner accounts based on their level. Learners will be able to upload images and documents to ask your question. Your questions will be used to retrieve other similar questions from the database to guide you in your learning. In the Basic account, each parent will only have access to one learner account. Learners can continue to ask questions using text only. There will be no suggested questions provided for learners.',
    },
    {
      question: 'How can I teach my child to use AI?',
      answer:
        "As EdTech enthusiasts, we see it as our responsibility to explore how AI can transform the future of education. We believe that AI has great potential to personalise learning for every child. It's our mission to give every child access to quality teaching, but we are also well-aware of the risks. MYAi is ethically designed with safety and learning as a top priority. Guardrails are in place to help your child focus on learning. Parents have full access to the chat history and will be able to flag messages to us when inappropriate interactions occur. At MYAi, we are parents too.",
    },
  ];

  return (
    <div className='h-full flex flex-col justify-center'>
      <Text className='mb-4 text-lg md:text-xl lg:text-2xl font-bold text-center'>
        Frequently Asked Questions
      </Text>

      <div className='flex flex-col items-center justify-center mb-8'>
        <Text
          align='center'
          color='dimmed'
          className=' text-xs md:text-md lg:text-base'
        >
          For any queries, reach out to us at support@myaiconsult.com.
        </Text>
        <div className='flex flex-row gap-2 mt-2 items-center justify-center'>
          <Text
            onClick={() => {
              window.open('https://wa.me/6597766164', '_blank');
            }}
            component='a'
            align='center'
            className=' cursor-pointer underline text-green-600 text-xs md:text-md lg:text-base'
          >
            Whatsapp Us!
          </Text>
          <Icon name='whatsapp' color='green' size={'20'} />
        </div>
      </div>

      <div className='overflow-y-auto max-h-[60vh] md:max-h-[70vh] pr-2'>
        <Accordion>
          {faqs.map((faq, index) =>
            index != 2 ? (
              <Accordion.Item key={index} value={`item-${index}`}>
                <Accordion.Control className='text-xs md:text-md lg:text-base'>
                  {faq.question}
                </Accordion.Control>
                <Accordion.Panel className='text-xs md:text-md lg:text-base'>
                  {faq.answer}
                </Accordion.Panel>
              </Accordion.Item>
            ) : (
              <Accordion.Item key={index} value={`item-${index}`}>
                <Accordion.Control className='text-xs md:text-md lg:text-base'>
                  {faq.question}
                </Accordion.Control>
                <Accordion.Panel className='text-xs md:text-md lg:text-base'>
                  <Text>
                    You may create an account with MYAi through the{' '}
                    <a
                      className='underline cursor-pointer text-blue-500'
                      href={'https://chat.myaiconsult.com/auth/signup'}
                    >
                      Sign up
                    </a>{' '}
                    to access the Premium Account for free for 7 days. After the
                    7 day free trial, users will get access to the Basic
                    Account. Subscribe to the monthly or annual plans, to
                    continue to use the Premium Account.
                  </Text>
                </Accordion.Panel>
              </Accordion.Item>
            )
          )}
        </Accordion>
      </div>
    </div>
  );
};

const Footer: React.FC = () => {
  const { isMobile } = useResponsive();

  return (
    <footer className='bg-primary text-white py-8'>
      <Container size='xl'>
        <Group position='center' spacing='md' className='mb-2'>
          <div className='flex flex-col justify-center items-center'>
            <Group position='center' spacing='md' className='mb-2'>
              <ActionIcon
                className=' hover:bg-primary'
                onClick={() => {
                  window.open(
                    'https://www.facebook.com/profile.php?id=61564846074866&sk=about',
                    '_blank'
                  );
                }}
                size='lg'
                variant='subtle'
                color={'white'}
              >
                <Icon name='facebook' color='white' size={'28'} />
              </ActionIcon>
              <ActionIcon
                className=' hover:bg-primary'
                onClick={() => {
                  window.open(
                    'https://www.instagram.com/my_aiconsult/',
                    '_blank'
                  );
                }}
                size='lg'
                variant='subtle'
                color={'white'}
              >
                <Icon name='instagram' color='white' size={'28'} />
              </ActionIcon>
              {/* <ActionIcon onClick={() => {
             window.open('https://www.instagram.com/my_aiconsult/', '_blank');
          }} size="lg" variant="subtle" color={"white"} >
            <Icon name='twitter' color='white' size={'28'} />
          </ActionIcon> */}
              {!isMobile && (
                <Icon name='pin' className=' ml-4' color='white' size={'28'} />
              )}
              <div className='flex flex-row items-center mb-2 w-40'>
                {isMobile && (
                  <Icon
                    name='pin'
                    className=' ml-4'
                    color='white'
                    size={'28'}
                  />
                )}
                <Text
                  align='center'
                  className='text-xs text-white md:text-md lg:text-base'
                >
                  10 Anson Road, #22-02, International Plaza, Singapore (079903)
                </Text>
              </div>
            </Group>

            <Text
              align='center'
              className='text-xs text-white md:text-md lg:text-base'
            >
              MYAi Consult Pte Ltd (UEN: 202435444M). All rights reserved.
            </Text>
          </div>
        </Group>
      </Container>
    </footer>
  );
};

export default LandingPage;
